
import Vue from "vue";
import { Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import MemberModule from "@/store/modules/MemberModule";
import { Member } from "@/models/Member";
import ContentModule from "@/store/modules/ContentModule";
import FilterModule from "@/store/modules/FilterModule";
import $ from "jquery";
import { VariantFilter } from "@/models/VariantFilter";
import { Route } from "vue-router";
import { Helpers } from "@/global-scripts/helpers";
import { CategoryModel } from "@/models/CategoryModel";
import { TopicModel } from "@/models/TopicModel";
import PrintBasketModule from "@/store/modules/PrintBasketModule";
import SearchModule from "@/store/modules/SearchModule";
import {ValidityDomains} from "@/settings/ValidityDomains";

export default abstract class AppBase extends Vue {
  protected contentModule = getModule(ContentModule, this.$store);
  protected filterModule = getModule(FilterModule, this.$store);
  protected memberModule = getModule(MemberModule, this.$store);
  protected printBasketModule = getModule(PrintBasketModule, this.$store);
  protected searchModule = getModule(SearchModule, this.$store);

  private get allFiltersSet(): boolean {
    return this.filterModule.allFiltersSet;
  }
  
  private get platform(): string {
    return this.filterModule.platform;
  }

  private get allDataLoaded(): boolean {
    return this.contentModule.allDataLoaded;
  }

  private get market(): VariantFilter | undefined {
    return this.filterModule.market;
  }

  private get model(): VariantFilter | undefined {
    return this.filterModule.model;
  }

  private get structureWeek(): VariantFilter | undefined {
    return this.filterModule.structureWeek;
  }

  private get propulsion(): VariantFilter | undefined {
    return this.filterModule.propulsion;
  }

  private get searchModalOpen(): boolean {
    return this.searchModule.searchModalOpen;
  }

  async onCreateSetup() {
    this.setMember();
    this.setLocale();

    this.bootstrap();

    setTimeout(() => {
      Helpers.loadingStart();
    }, 200);

    await this.filterModule.fetchAllValidities();
    this.setFiltersFromUrl();
    await this.fetchFilters();

    Helpers.loadingComplete();
  }

  /**
   *  Set active filters from url parameters.
   *
   **/
  private setFiltersFromUrl() {
    const market = this.$route.params.market;
    const model = this.$route.params.model;
    const structureWeek = this.$route.params.structureWeek;
    const propulsion = this.$route.params.propulsion;
    const electricCapacity = this.$route.params.electricCapacity;
    const electricPowertrain = this.$route.params.electricPowertrain;
    const additionalFiltersInUrl = electricCapacity && electricCapacity.length > 0;

    // Url did not include all the needed filters
    if (!market || !model || !structureWeek || !propulsion) {
      this.goToStartPage();
      return;
    }

    const allValidities = this.filterModule.allValidities;

    const marketEntry = this.$validityHelper.getValidityEntryByUrlFriendlyTitle(
      allValidities,
        ValidityDomains.market,
      market
    );

    const modelEntry = this.$validityHelper.getValidityEntryByUrlFriendlyTitle(
      allValidities,
      ValidityDomains.model,
      model
    );

    const structureWeekEntry = this.$validityHelper.getValidityEntryByUrlFriendlyTitle(
      allValidities,
      ValidityDomains.structureWeek,
      structureWeek
    );
    const propulsionEntry = this.$validityHelper.getValidityEntryByUrlFriendlyTitle(
      allValidities,
      ValidityDomains.propulsion,
      propulsion
    );

    const electricCapacityEntry = this.$validityHelper.getValidityEntryByUrlFriendlyTitle(
      allValidities,
      ValidityDomains.electricCapacity,
      electricCapacity
    );

    const electricPowertrainEntry = this.$validityHelper.getValidityEntryByUrlFriendlyTitle(
      allValidities,
      ValidityDomains.electricPowertrain,
      electricPowertrain
    );

    const validAdditionalFilters = electricCapacityEntry && electricPowertrainEntry;

    if (
      !marketEntry ||
      !modelEntry ||
      !structureWeekEntry ||
      !propulsionEntry ||
      (additionalFiltersInUrl && !validAdditionalFilters)
    ) {
      // console.error("One or more filter in url is not valid", {
      //   marketEntry,
      //   modelEntry,
      //   structureWeekEntry,
      //   propulsionEntry,
      //   electricCapacityEntry,
      //   electricPowertrainEntry,
      // });

      this.goToStartPage();
      return;
    }

    this.filterModule.setMarket(new VariantFilter(ValidityDomains.market, marketEntry));
    this.filterModule.setModel(
      new VariantFilter(ValidityDomains.model, modelEntry)
    );
    this.filterModule.setStructureWeek(
      new VariantFilter(ValidityDomains.structureWeek, structureWeekEntry)
    );
    this.filterModule.setPropulsion(new VariantFilter(ValidityDomains.propulsion, propulsionEntry));

    if (electricCapacityEntry && electricPowertrainEntry) {
      this.filterModule.setElectricCapacity(
        new VariantFilter(ValidityDomains.electricCapacity, electricCapacityEntry)
      );
      this.filterModule.setElectricPowertrain(
        new VariantFilter(ValidityDomains.electricPowertrain, electricPowertrainEntry)
      );
    }
  }

  private setMember() {
    this.memberModule.setMember((window as any).memberInfo as Member);
  }

  private setLocale() {
    const lang = this.$route.params.lang;
    this.contentModule.setLocale(lang);

    const noneLatinLanguage = ["ru", "th", "ko", "zh", "ja"];

    const langParts = lang.split("-");
    if (langParts.length) {
      $("html").attr("lang", langParts[0]).attr("xml:lang", langParts[0]);

      if (noneLatinLanguage.indexOf(langParts[0]) !== -1) {
        $("html").addClass("none-latin");
      }
    }
  }

  private bootstrap() {
    const html = document.documentElement; // $("html");
    const body = document.body;
    const $win = $(window);

    // Set some browser compatibility helper classes
    let browser = Helpers.getBrowser();
    if (browser) browser = browser.replace(".", "-").replace(" ", "");

    html.classList.remove("no-js");
    html.classList.add("js");
    html.classList.add(browser);

    // Check if webkit browser
    if ("WebkitAppearance" in document.documentElement.style) {
      html.classList.add("webkit");
    }

    if (!("ontouchstart" in document.documentElement)) {
      html.classList.add("no-touch");
    } else {
      html.classList.add("touch");
    }

    // Detect desktop safari
    //if (GlobalHelpers.isDesktopSafari()) {
    //    $html.addClass('desktop-safari');
    //}

    // Detect mobile safari to account for 44px ui bar
    if (Helpers.isMobileSafari()) {
      html.classList.add("mobile-safari");
    }

    // Microsoft stuff
    if (Helpers.isMicrosoftUser()) {
      html.classList.add("ie-" + Helpers.getInternetExplorerVersion());
      html.classList.add("ms-user");
      html.classList.add("no-touch"); // can't predict touch screen on windows machines;

      const ieVersion = parseInt(Helpers.getInternetExplorerVersion().toString(), 10);

      if (ieVersion !== 0 && ieVersion <= 11) {
        html.classList.add("lte-ie11");
      }
      if (ieVersion !== 0 && ieVersion < 11) {
        html.classList.add("lt-ie11");
      }
    }

    // Set initiated class (to enable transitions, among many things)
    body.classList.add("initiated");

    // Add transitions class efter everything has loaded, 1 second to be exact ;)
    setTimeout(() => {
      body.classList.add("transitions");
    }, 1000);

    // Bind window focus classes
    $win
      .on("focus", function () {
        body.classList.add("is-focused");
      })
      .on("blur", function () {
        body.classList.add("is-focused");
      });

    // Bind scroll cancel events
    $win.on("DOMMouseScroll mousewheel", Helpers.cancelScrollTo);
    $(document).on("touchmove", Helpers.cancelScrollTo);

    // Attach router behavior on "App name" link
    $("#header-start-link").on("click", (e) => {
      e.preventDefault();
      this.$router.push({ path: `/${this.contentModule.locale}/` });
    });

    // Attach router behavior on "App name" link
    $("#header-logo a").on("click", (e) => {
      e.preventDefault();
      this.$router.push({ path: `/${this.contentModule.locale}/` });
    });
  }

  /**
   * Will fetch all validities if previously selected filters exists.
   */
  private async fetchFilters() {
    await this.fetchMarkets();
    this.filterModule.setInitialFetchComplete(true);
  }

  private resetFilters(domain: string) {
    this.filterModule.resetFilters(domain);
    this.goToStartPage();
  }

  private async fetchMarkets() {
    await this.filterModule.fetchMarkets();

    if (this.$validityHelper.isValidFilter(this.filterModule.markets, this.filterModule.market)) {
      await this.fetchModels();
    } else if (this.$validityHelper.shouldResetFilterIfInvalid(this.filterModule.markets, this.filterModule.market)) {
      this.resetFilters(ValidityDomains.market);
    }
  }

  private async fetchModels() {
    await this.filterModule.fetchModels();

    if (this.$validityHelper.isValidFilter(this.filterModule.models, this.filterModule.model)) {
      await this.fetchStructureWeeks();
    } else if (this.$validityHelper.shouldResetFilterIfInvalid(this.filterModule.models, this.filterModule.model)) {
      this.resetFilters(ValidityDomains.model);
    }
  }

  private async fetchStructureWeeks() {
    await this.filterModule.fetchStructureWeeks();

    if (this.$validityHelper.isValidFilter(this.filterModule.structureWeeks, this.filterModule.structureWeek)) {
      await this.fetchAdditionalFilters();
    } else if (
      this.$validityHelper.shouldResetFilterIfInvalid(this.filterModule.structureWeeks, this.filterModule.structureWeek)
    ) {
      this.resetFilters(ValidityDomains.structureWeek);
    }
  }

  private async fetchAdditionalFilters() {
    await this.filterModule.fetchAdditionalFilters();

    const validPropulsion = this.$validityHelper.isValidFilter(
      this.filterModule.propulsions,
      this.filterModule.propulsion
    );
    const validElectricCapacity = this.$validityHelper.isValidFilter(
      this.filterModule.electricCapacities,
      this.filterModule.electricCapacity
    );
    const validElectricPowertrain = this.$validityHelper.isValidFilter(
      this.filterModule.electricPowertrains,
      this.filterModule.electricPowertrain
    );

    if (
      !validPropulsion &&
      this.$validityHelper.shouldResetFilterIfInvalid(this.filterModule.propulsions, this.filterModule.propulsion)
    ) {
      this.resetFilters(ValidityDomains.propulsion);
    } else if (
      !validElectricCapacity &&
      this.$validityHelper.shouldResetFilterIfInvalid(
        this.filterModule.electricCapacities,
        this.filterModule.electricCapacity
      )
    ) {
      this.resetFilters(ValidityDomains.electricCapacity);
    } else if (
      !validElectricPowertrain &&
      this.$validityHelper.shouldResetFilterIfInvalid(
        this.filterModule.electricPowertrains,
        this.filterModule.electricPowertrain
      )
    ) {
      this.resetFilters(ValidityDomains.electricPowertrain);
    }
  }

  private goToStartPage() {
    if (this.$route.name !== "Select Variant") {
      this.$router.push({ path: `/${this.contentModule.locale}` });
    }
  }

  @Watch("market")
  @Watch("model")
  @Watch("structureWeek")
  private async onFilterChange(newVal: VariantFilter | undefined, oldVal: VariantFilter | undefined) {
    if (!this.filterModule.initialFetchComplete) {
      return;
    }
    
    if (!newVal || (oldVal && newVal.entry === oldVal.entry)) {
      return;
    }

    switch (newVal.domain) {
      case ValidityDomains.market:
        await this.fetchModels();
        break;
      case ValidityDomains.model:
        await this.fetchStructureWeeks();
        break;
      case ValidityDomains.structureWeek:
        await this.fetchAdditionalFilters();
        break;
    }
  }

  /**
   * Recursive category active finder
   */
  findCategoryByUrlName(title: string, categories: CategoryModel[]): CategoryModel | undefined {
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      if (Helpers.urlFriendlyName(category.title) === title.toLowerCase()) {
        return category;
      } else if (category.nodes) {
        const foundCat = this.findCategoryByUrlName(title, category.nodes);
        if (foundCat) {
          return foundCat;
        }
      }
    }
    return undefined;
  }

  private categoryByUrlName(
    urlName: string,
    parentCategory: CategoryModel | undefined = undefined
  ): CategoryModel | undefined {
    const categories = parentCategory ? parentCategory.nodes : this.contentModule.filteredCategories;
    return this.findCategoryByUrlName(urlName, categories); //getters.filteredCategories.find(category => category.id === id)
  }

  private topicByUrlName(urlName: string, category: CategoryModel): TopicModel | undefined {
    const topics = category.topics;

    let topic;
    if (topics) {
      topic = topics.find((t) => Helpers.urlFriendlyName(t.title) === urlName);
    }

    if (!topic && category.nodes && category.nodes) {
      for (let i = 0; i < category.nodes.length; i++) {
        topic = this.topicByUrlName(urlName, category.nodes[i]);

        if (topic) {
          return topic;
        }
      }
    }

    return topic;
  }

  private setActiveCategoriesFromParam() {
    const topCategory = this.categoryByUrlName(this.$route.params.cat);
    if (topCategory) {
      this.contentModule.setActiveTopCategory(topCategory);

      const subCategory = this.$route.params.subCat
        ? this.categoryByUrlName(this.$route.params.subCat, topCategory)
        : undefined;

      this.contentModule.setActiveSubCategory(subCategory);
      // Expand category
      this.$nextTick(() => {
        this.contentModule.setExpandedCategory(this.contentModule.activeTopCategory);
      });
    }
  }

  private setDataFromParams() {
    if (!this.allDataLoaded) {
      // Wait until data has been loaded
      return;
    }

    // Update Category ID
    if (this.$route.params.cat) {
      this.setActiveCategoriesFromParam();
    } else {
      this.contentModule.setActiveSubCategory(undefined);
      this.contentModule.setActiveTopCategory(undefined);
      this.contentModule.setExpandedCategory(undefined);
    }

    // Set Topic ID
    if (this.$route.params.topic) {
      const category = this.contentModule.activeCategory;
      const topic = category ? this.topicByUrlName(this.$route.params.topic, category) : undefined;
      if (topic) {
        this.contentModule.setActiveTopic(topic);
      } else {
        console.error("Unable to find topic in route");
      }
    } else {
      this.contentModule.setActiveTopic(undefined);
    }
  }

  @Watch("allFiltersSet")
  private async onAllFiltersSetChange(newVal: boolean) {
    if (newVal && !this.contentModule.allDataLoaded) {
      await this.contentModule.fetchAllTopics(this.filterModule.platform);
      await this.contentModule.fetchAllCategories(this.filterModule.platform);
      this.setDataFromParams();

      await this.printBasketModule.fetchPrintBasket();
    }
  }

  @Watch("platform")
  private async onPlatformChange(newVal: string, oldVal: string) {
    // Only need to fetch again if already fetch for earlier platform
    if (newVal && oldVal) {
      await this.contentModule.fetchAllTopics(this.filterModule.platform);
      await this.contentModule.fetchAllCategories(this.filterModule.platform);
    }
  }

  @Watch("searchModalOpen")
  private onSearchModalOpenChange(newVal: boolean) {
    if (newVal) {
      $("body").addClass("search-opened");
    } else {
      $("body").removeClass("search-opened");
    }
  }

  @Watch("$route")
  private onRouteBaseChange(to: Route, from: Route) {
    // Reset all active state when going to Select Variant page
    if (to.name === "Select Variant") {
      this.contentModule.setActiveSubCategory(undefined);
      this.contentModule.setActiveTopic(undefined);
      this.contentModule.setExpandedCategory(undefined);
      Helpers.cancelScrollTo();
      Helpers.scrollTo("body", 0, { animTime: 250 });

      // Remove search-opened state
      $("body").removeClass("search-opened");
    }

    if (to.name !== "PrintPreview") {
      $("body").removeClass("print-preview");
    }

    if (this.filterModule.allFiltersSet) {
      this.setDataFromParams();
    }

    this.$tracker.setGAPageUrl(this.filterModule.activeFilters, this.$route);
  }
}
